<template>
  <div>
    <div>
      <div class="flex align-center mb-5">
        <div class="flex box align-center">
          <el-input
            size="medium"
            v-model="search"
            placeholder="Search"
            style="max-width: 190px"
          ></el-input>
          <el-button class="ml-15" @click="addHoliday"> Add New </el-button>
        </div>

        <div v-if="records.length && !search" class="box right">
          <el-pagination
            layout="prev, pager, next"
            small
            background
            :current-page="page"
            :page-size="recordsPerPage"
            :pager-count="5"
            :total="totalRecords"
            @current-change="pageSet"
            @next-click="pageNext"
            @prev-click="pagePrevious"
          />
        </div>
      </div>

      <el-tag size="medium" class="mb-5">
        <i class="el-icon-info" style="margin-right: 4px" />
        Total: {{ totalRecords }}
      </el-tag>

      <div
        v-if="records.length"
        id="holidays-list"
        class="black-text"
        v-loading="holidaysLoading"
        style="max-height: 600px; overflow-y: auto"
      >
        <hr />

        <div v-for="(item, index) in records" :key="index">
          <div class="holiday">
            <div class="holiday__info">
              <h3 class="holiday__title">{{ item.name }}</h3>

              <p class="holiday__label">
                {{ getRecurrencePatternText(item) }}
              </p>
              <p v-if="item.startDate && item.endDate" class="holiday__label">
                {{ formatDateRange(item.startDate, item.endDate) }}
              </p>
              <p v-if="item.notifyDaysBefore" class="holiday__label">
                {{ item.notifyDaysBefore }} days notice
              </p>
            </div>

            <div class="holiday__hours">
              <h3 v-if="item.isClosed" class="mb-0 mt-0 voxo-red">Closed</h3>
              <div class="mb-5" v-else>
                <h3 class="holiday__title">Hours</h3>
                <p
                  v-for="({ startTime, endTime }, idx) in item.operatingHours"
                  :key="`${startTime}-${idx}`"
                  class="holiday__hour"
                >
                  {{ startTime }} to {{ endTime }}
                </p>
              </div>
            </div>
            <div style="margin-left: auto">
              <el-tooltip placement="top" content="View Excpetion">
                <el-button
                  class="table-action-button"
                  icon="el-icon-edit"
                  @click="viewHoliday(item)"
                />
              </el-tooltip>

              <el-tooltip placement="top" content="Remove Exception">
                <el-button
                  class="table-action-button"
                  type="danger"
                  icon="el-icon-delete"
                  @click="removeHoliday(item.id)"
                />
              </el-tooltip>
            </div>
          </div>

          <hr />
        </div>
      </div>
      <div v-else class="black-text">
        <p>No special holidays found!</p>
      </div>
    </div>

    <holidays-form
      :context="context"
      :visible="visible"
      :selectedRecord="selectedHoliday"
      :businessHoursId="businessHoursId"
      :getRecurrencePatternText="getRecurrencePatternText"
      @handleClose="handleClose"
      @cancelOperation="cancelOperation"
    />
  </div>
</template>

<script>
import { debounce } from "lodash";
import DadsSelect from "@/components/select";
import HolidaysForm from "./holidaysForm.vue";
import MultiTimeRangeSelector from "./multiTimeRangeSelector.vue";
import { getHolidays, deleteHoliday } from "@/api/holidays/methods.js";
import { DateTime } from "luxon";

export default {
  name: "HolidaysList",

  props: {
    businessHoursId: { required: true }
  },

  components: {
    DadsSelect,
    HolidaysForm,
    MultiTimeRangeSelector
  },

  data() {
    return {
      context: "Create",
      selectedHoliday: null,
      visible: false,
      search: "",
      holidaysLoading: false,
      records: [],
      page: 1,
      maxPage: 1,
      totalRecords: 0,
      recordsPerPage: 75,
      monthMap: new Map([
        [1, "January"],
        [2, "February"],
        [3, "March"],
        [4, "April"],
        [5, "May"],
        [6, "June"],
        [7, "July"],
        [8, "August"],
        [9, "September"],
        [10, "October"],
        [11, "November"],
        [12, "December"]
      ]),
      dayOfWeekMap: new Map([
        [0, "Sunday"],
        [1, "Monday"],
        [2, "Tuesday"],
        [3, "Wednesday"],
        [4, "Thursday"],
        [5, "Friday"],
        [6, "Saturday"]
      ]),
      weekOfMonthMap: new Map([
        [1, "First"],
        [2, "Second"],
        [3, "Third"],
        [4, "Fourth"],
        [5, "Last"]
      ])
    };
  },

  computed: {
    selectedTenant() {
      return this.$store.state.selectedTenant;
    }
  },

  watch: {
    search: debounce(function () {
      this.searchRecords();
    }, 300),

    businessHoursId: {
      immediate: true,
      handler(id) {
        if (id) {
          this.searchRecords();
        }
      }
    }
  },

  methods: {
    handleClose() {
      this.visible = false;
      this.selectedHoliday = null;
    },

    async searchRecords() {
      this.holidaysLoading = true;
      this.records = [];
      const { success, data } = await getHolidays({
        tenantId: this.selectedTenant.id,
        businessHoursId: this.businessHoursId,
        name: this.search
      });

      this.holidaysLoading = false;

      if (success) {
        const { records, totalRecords, maxPage } = data;
        this.totalRecords = totalRecords;
        this.maxPage = maxPage;
        this.records = [...records];
      }
    },

    pageNext() {
      if (this.page < this.maxPage) {
        this.page += 1;
      }
    },

    pagePrevious() {
      if (this.page > 1) {
        this.page -= 1;
      }
    },

    pageSet(page) {
      this.page = page;
    },

    getOrdinalSuffix(num) {
      const j = num % 10;
      const k = num % 100;

      if (j === 1 && k !== 11) {
        return num + "st";
      }
      if (j === 2 && k !== 12) {
        return num + "nd";
      }
      if (j === 3 && k !== 13) {
        return num + "rd";
      }
      return num + "th";
    },

    // Get weekday name
    getDayOfWeekName(day) {
      const days = [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday"
      ];
      return days[day];
    },

    // Get month name
    getMonthName(month) {
      if (!month) return "";
      const months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December"
      ];
      return months[month - 1];
    },

    formatDateRange(startDate, endDate) {
      if (!startDate || !endDate) return "";

      const start = new Date(startDate);
      const end = new Date(endDate);

      const formatDate = (date) => {
        return date.toISOString().split("T")[0]; // Returns YYYY-MM-DD format
      };

      const startFormatted = formatDate(start);
      const endFormatted = formatDate(end);

      if (startFormatted === endFormatted) {
        return startFormatted;
      }

      return `${startFormatted} through ${endFormatted}`;
    },
    convertToLuxonDayOfWeek(dayOfWeek) {
      return dayOfWeek === 0 ? 7 : dayOfWeek;
    },
    calculateFloatingHolidayDate(year, month, dayOfWeek, weekOfMonth) {
      // Find the first day of the specified month
      let dt = DateTime.fromObject({ year, month, day: 1 });

      // Convert to Luxon day format
      const luxonDayOfWeek = this.convertToLuxonDayOfWeek(dayOfWeek);

      // Find first occurrence of the day in the month
      while (dt.weekday !== luxonDayOfWeek) {
        dt = dt.plus({ days: 1 });
      }

      // Add weeks to get to the desired week of month
      return dt.plus({ weeks: weekOfMonth - 1 });
    },

    isLastWeekdayOfMonth(date) {
      // Check if adding 7 days would put us in the next month
      const nextWeek = date.plus({ days: 7 });
      return nextWeek.month !== date.month;
    },

    getRecurrencePatternText(holiday) {
      if (!holiday.isRecurring) return "";

      const isLastOccurrenceInMonth = (dayOfWeek, weekOfMonth, month) => {
        if (dayOfWeek === null || weekOfMonth === null) return false;

        // Use current year and month (or specified month) for calculation
        const year = DateTime.now().year;
        const monthToCheck = month || DateTime.now().month;

        const date = this.calculateFloatingHolidayDate(
          year,
          monthToCheck,
          dayOfWeek,
          weekOfMonth
        );
        return this.isLastWeekdayOfMonth(date);
      };

      const getDayOfWeekName = (day) => {
        const days = [
          "Sunday",
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday"
        ];
        return days[day];
      };

      // Get month name
      const getMonthName = (month) => {
        if (!month) return "";
        const months = [
          "January",
          "February",
          "March",
          "April",
          "May",
          "June",
          "July",
          "August",
          "September",
          "October",
          "November",
          "December"
        ];
        return months[month - 1];
      };

      const { day, dayOfWeek, weekOfMonth, month } = holiday;

      // Weekly pattern
      if (dayOfWeek !== null && weekOfMonth === null && month === null) {
        return `Weekly on ${this.getDayOfWeekName(dayOfWeek)}`;
      }

      // Monthly by day number
      if (day !== null && dayOfWeek === null && month === null) {
        return `Monthly on the ${this.getOrdinalSuffix(day)}`;
      }

      // Monthly by weekday position
      if (dayOfWeek !== null && weekOfMonth !== null && month === null) {
        const isLast = weekOfMonth === 5;
        const weekText = isLast ? "last" : this.getOrdinalSuffix(weekOfMonth);
        return `Monthly on the ${weekText} ${getDayOfWeekName(dayOfWeek)}`;
      }


      // Annually by date
      if (day !== null && month !== null && dayOfWeek === null) {
        return `Annually on ${this.getMonthName(month)} ${this.getOrdinalSuffix(
          day
        )}`;
      }

      // Annually by weekday position
      if (dayOfWeek !== null && weekOfMonth !== null && month !== null) {
        const isLast = weekOfMonth === 5;
        const weekText = isLast ? "last" : this.getOrdinalSuffix(weekOfMonth);
        return `Annually on the ${weekText} ${getDayOfWeekName(
          dayOfWeek
        )} of ${getMonthName(month)}`;
      }

      return "Recurring";
    },

    addHoliday() {
      this.context = "Create";
      this.visible = true;
    },

    viewHoliday(holiday) {
      this.visible = true;
      this.context = "Edit";
      this.selectedHoliday = holiday;
    },

    async removeHoliday(id) {
      try {
        await this.$confirm("Process Exception delete?", "Warning", {
          confirmButtonText: "Yes",
          cancelButtonText: "Cancel"
        });

        this.processing = true;
        const { success, data } = await deleteHoliday({ id });
        this.processing = false;

        if (success) {
          const index = this.records.findIndex((item) => item.id === id);
          if (index !== -1) {
            this.records.splice(index, 1);
          }
        }
      } catch (err) {
        this.processing = false;
      }
    },

    cancelOperation() {
      this.visible = false;
      this.selectedHoliday = null;
      this.searchRecords();
    }
  }
};
</script>

<style lang="scss" scoped>
.holiday {
  display: flex;
  padding: 10px;

  &__info {
    width: 300px;
  }

  &__hours {
    margin-left: 10px;
  }

  &__title,
  &__label,
  &__hour {
    margin: 0;
  }

  &__label {
    font-size: 12px;
    font-style: italic;
  }

  &__hour {
    font-size: 13px;
  }
}
</style>
